import {
  Table,
  Tag,
  Input,
  Button,
  Row,
  Col,
  Typography,
  Card,
  Space,
  Spin,
  Menu,
  Dropdown,
  Statistic,
  Modal,
  Form,
  Select,
  InputNumber,
  DatePicker,
  Radio,
  message,
} from "antd";
import {
  EyeOutlined,
  UserAddOutlined,
  SearchOutlined,
  DollarOutlined,
  EllipsisOutlined,
  LineChartOutlined,
  UserOutlined,
  AlertOutlined,
  DownloadOutlined,
  FundOutlined,
  RiseOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import React, { useEffect, useState, useContext, useMemo } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { AppContext } from "../context/AppContext";
import { TransactionHooks } from "../utils/transactionHooks";
import {
  calculateMaturityAmounts,
  calculateAccruedValue,
  toNumber,
} from "../utils/investmentCalculations";
import "./Investors.css";

const { Title, Text } = Typography;

// Wrapper that routes company 22 to the Jutem-specific investors page
const InvestorsRouter = () => {
  const { company } = useContext(AppContext);

  if (company?.id === 22) {
    return <JutemInvestorsView />;
  }

  return <Investors />;
};

const Investors = () => {
  const token = localStorage.getItem("token");
  const [investors, setInvestors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const { company, accounts, updateAccountBalance } = useContext(AppContext);
  const navigate = useNavigate();
  const [showInvestorModal, setShowInvestorModal] = useState(false);
  const [showInvestmentModal, setShowInvestmentModal] = useState(false);
  const [investorSource, setInvestorSource] = useState("new");
  const [borrowers, setBorrowers] = useState([]);
  const [investmentProducts, setInvestmentProducts] = useState([]);
  const [investorRoleId, setInvestorRoleId] = useState(null);
  const [investorProfiles, setInvestorProfiles] = useState([]);
  const [companyInvestments, setCompanyInvestments] = useState([]);
  const [creating, setCreating] = useState(false);
  const [investorForm] = Form.useForm();
  const [investmentForm] = Form.useForm();

  const getSummaryStats = () => {
    // Flatten all investments from all investors
    const allInvestments = mergedInvestors.flatMap(
      (investor) => investor.investments || []
    );

    const totalInvestors = mergedInvestors.length;
    const totalCapital = allInvestments.reduce(
      (sum, inv) => sum + (inv.principal_amount || 0),
      0
    );
    const totalInvestments = allInvestments.length;
    const avgInvestment =
      totalInvestments > 0 ? totalCapital / totalInvestments : 0;
    const activeInvestments = allInvestments.filter(
      (inv) => inv.status === "Active"
    ).length;

    return (
      <Row gutter={24} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="Total Investors"
              value={totalInvestors}
              valueStyle={{ color: "#389e0d" }}
              prefix={<UserOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Total Capital"
              value={totalCapital}
              prefix="ZMW"
              valueStyle={{ color: "#1890ff" }}
              formatter={(value) => value.toLocaleString()}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Avg. Investment"
              value={avgInvestment}
              prefix="ZMW"
              valueStyle={{ color: "#faad14" }}
              formatter={(value) => Math.round(value).toLocaleString()}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Active Investments"
              value={activeInvestments}
              valueStyle={{ color: "#cf1322" }}
              prefix={<AlertOutlined />}
            />
          </Card>
        </Col>
      </Row>
    );
  };

  const columns = [
    {
      title: "INVESTOR",
      dataIndex: "first_name",
      render: (_, record) => (
        <div>
          <div
            style={{ fontWeight: 500 }}
          >{`${record.first_name} ${record.last_name}`}</div>
          <div style={{ color: "#8c8c8c", fontSize: 12 }}>
            NRC: {record.nrc}
          </div>
        </div>
      ),
      sorter: (a, b) => a.first_name.localeCompare(b.first_name),
    },
    {
      title: "CONTACT",
      render: (_, record) => (
        <div className="contact-info">
          <div>{record.email}</div>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {record.phone}
          </Text>
        </div>
      ),
    },
    {
      title: "INVESTMENTS",
      render: (_, record) => <div>{record.investments.length}</div>,
    },
    {
      title: "TOTAL INVESTED",
      render: (_, record) => (
        <Text strong style={{ color: "#1890ff" }}>
          ZMW{(record.total_invested || 0).toLocaleString()}
        </Text>
      ),
    },
    {
      title: "INVESTOR STATUS",
      dataIndex: "status",
      render: (status) => (
        <Tag
          color={status === "active" ? "#87d068" : "#f50"}
          style={{ borderRadius: 15, padding: "0 10px" }}
        >
          {status.toUpperCase()}
        </Tag>
      ),
      filters: [
        { text: "Active", value: "active" },
        { text: "Inactive", value: "inactive" },
      ],
      onFilter: (value, record) => record.status === value,
    },
    {
      title: "",
      key: "action",
      render: (_, record) => {
        const menu = (
          <Menu>
            {record.investments?.length > 0 ? (
              record.investments.map((investment) => (
                <Menu.Item
                  key={investment.id}
                  onClick={() => navigate(`/investment/${investment.id}`)}
                >
                  Investment #{investment.id}
                </Menu.Item>
              ))
            ) : (
              <Menu.Item disabled>No investments</Menu.Item>
            )}
          </Menu>
        );

        return (
          <Dropdown overlay={menu} trigger={["click"]}>
            <Button
              shape="circle"
              icon={<EllipsisOutlined style={{ color: "#1890ff" }} />}
              style={{ border: "none" }}
            />
          </Dropdown>
        );
      },
    },
  ];

  useEffect(() => {
    if (!company?.id) return;
    fetchInvestors();
    fetchBorrowers();
    fetchInvestmentProducts();
    fetchInvestorRole();
    fetchInvestorProfiles();
    fetchCompanyInvestments();
  }, [company?.id]);

  const fetchInvestorRole = async () => {
    try {
      const res = await axios.get(
        "https://zedlenders.pickmesms.com/roles",
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const investorRole = (res.data.data || []).find(
        (role) => role.name === "Investor"
      );
      if (investorRole) setInvestorRoleId(investorRole.id);
    } catch (err) {
      console.error("Error fetching roles:", err);
    }
  };

  const fetchInvestors = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `https://zedlenders.pickmesms.com/users?filter[company]=${company.id}&filter[role][name][_eq]=Investor&fields=*,investments.*`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const processedData = response.data.data.map((investor) => ({
        ...investor,
        user_id: investor.id,
        profile_id: null,
      }));
      setInvestors(processedData);
    } catch (err) {
      console.error("Error fetching investors:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchBorrowers = async () => {
    try {
      const response = await axios.get(
        `https://zedlenders.pickmesms.com/users?filter[company]=${company.id}&filter[role][name][_eq]=Borrower&fields=id,first_name,last_name,phone,email,nrc,role`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setBorrowers(response.data.data || []);
    } catch (err) {
      console.error("Error fetching borrowers:", err);
    }
  };

  const fetchInvestmentProducts = async () => {
    try {
      const response = await axios.get(
        `https://zedlenders.pickmesms.com/items/investment_products?filter[company]=${company.id}&fields=*`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setInvestmentProducts(response.data.data || []);
    } catch (err) {
      console.error("Error fetching investment products:", err);
    }
  };

  const fetchInvestorProfiles = async () => {
    try {
      const response = await axios.get(
        `https://zedlenders.pickmesms.com/items/investor_profiles?filter[company]=${company.id}&fields=*,user.*`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setInvestorProfiles(response.data.data || []);
    } catch (err) {
      console.error("Error fetching investor profiles:", err);
    }
  };

  const fetchCompanyInvestments = async () => {
    try {
      const response = await axios.get(
        `https://zedlenders.pickmesms.com/items/investments?filter[company]=${company.id}&fields=*`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCompanyInvestments(response.data.data || []);
    } catch (err) {
      console.error("Error fetching investments:", err);
    }
  };

  const convertTermToDays = (termValue, termUnit) => {
    const value = toNumber(termValue);
    if (termUnit === "months") return value * 30;
    if (termUnit === "years") return value * 365;
    return value;
  };

  const handleCreateInvestor = async (values) => {
    try {
      setCreating(true);
      if (investorSource === "borrower") {
        const borrowerId = values.borrower_id;
        if (!borrowerId) {
          message.error("Please select a borrower");
          return;
        }

        const existingProfile = investorProfiles.find(
          (profile) => profile.user?.id === borrowerId
        );
        if (existingProfile) {
          message.error("This borrower already has an investor profile.");
          return;
        }

        await axios.post(
          "https://zedlenders.pickmesms.com/items/investor_profiles",
          {
            user: borrowerId,
            company: company.id,
            status: "active",
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        message.success("Investor profile created for borrower");
        setShowInvestorModal(false);
        investorForm.resetFields();
        fetchInvestorProfiles();
        fetchCompanyInvestments();
      } else {
        if (!investorRoleId) {
          message.error("Investor role not found. Please create an Investor role in Directus.");
          return;
        }

        const payload = {
          first_name: values.first_name,
          last_name: values.last_name,
          email: values.email,
          phone: values.phone,
          nrc: values.nrc,
          address: values.address,
          company: company.id,
          role: investorRoleId,
          status: "active",
          password: values.password || "123456",
        };

        const userRes = await axios.post("https://zedlenders.pickmesms.com/users", payload, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const createdUser = userRes.data.data;

        await axios.post(
          "https://zedlenders.pickmesms.com/items/investor_profiles",
          {
            user: createdUser.id,
            company: company.id,
            status: "active",
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        message.success("Investor created successfully");
        setShowInvestorModal(false);
        investorForm.resetFields();
        fetchInvestors();
        fetchInvestorProfiles();
      }
    } catch (err) {
      message.error(
        err.response?.data?.errors?.[0]?.message || "Failed to create investor"
      );
    } finally {
      setCreating(false);
    }
  };

  const handleCreateInvestment = async (values) => {
    try {
      setCreating(true);

      const investorSelection = values.investor_id;
      const [investorType, investorRef] = investorSelection.split(":");
      const selectedProfile =
        investorType === "profile"
          ? investorProfiles.find((profile) => String(profile.id) === investorRef)
          : null;
      const investorId =
        investorType === "profile"
          ? selectedProfile?.user?.id
          : investorRef;
      const investorProfileId =
        investorType === "profile" ? selectedProfile?.id : null;

      const product = investmentProducts.find((p) => p.id === values.investment_product);
      const accountId = values.account_id;

      if (!investorId || !product || !accountId) {
        message.error("Please select investor, product, and account");
        return;
      }

      const principal = toNumber(values.principal_amount);
      if (product.min_amount && principal < toNumber(product.min_amount)) {
        message.error(`Minimum investment for this product is ZMW ${toNumber(product.min_amount).toLocaleString()}`);
        return;
      }
      if (product.max_amount && principal > toNumber(product.max_amount)) {
        message.error(`Maximum investment for this product is ZMW ${toNumber(product.max_amount).toLocaleString()}`);
        return;
      }
      const termDays = convertTermToDays(product.term_days, product.term_unit);
      const investmentDate = values.investment_date.format("YYYY-MM-DD");
      const maturityDate = values.investment_date
        .clone()
        .add(termDays, "days")
        .format("YYYY-MM-DD");

      const interestRate = toNumber(product.interest_rate);
      const interestType = product.interest_type || "simple";
      const compoundFrequency = product.compound_frequency || "monthly";
      const withholdingTaxRate = toNumber(product.withholding_tax_rate || 15);

      const maturity = calculateMaturityAmounts({
        principal,
        annualRatePercent: interestRate,
        days: termDays,
        interestType,
        compoundFrequency,
        withholdingTaxRate,
      });

      const investmentPayload = {
        investor_id: investorId,
        investor_profile: investorProfileId,
        investment_product: product.id,
        principal_amount: principal,
        interest_rate: interestRate,
        interest_type: interestType,
        compound_frequency: compoundFrequency,
        term_days: termDays,
        investment_date: investmentDate,
        maturity_date: maturityDate,
        expected_interest: maturity.expectedInterest,
        withholding_tax_rate: withholdingTaxRate,
        withholding_tax_amount: maturity.withholdingTax,
        net_interest: maturity.netInterest,
        total_maturity_amount: maturity.grossMaturity,
        net_maturity_amount: maturity.netMaturity,
        status: "Active",
        account_id: accountId,
        company: company.id,
      };

      const createResponse = await axios.post(
        "https://zedlenders.pickmesms.com/items/investments",
        investmentPayload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const createdInvestment = createResponse.data.data;

      const account = accounts?.find((acc) => acc.id === accountId);
      if (account) {
        const newBalance = toNumber(account.balance) + principal;
        updateAccountBalance(accountId, newBalance);
        await axios.patch(
          `https://zedlenders.pickmesms.com/items/accounts/${accountId}`,
          { balance: newBalance },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }

      await axios.post(
        "https://zedlenders.pickmesms.com/items/transactions",
        {
          amount: principal,
          transaction_date: investmentDate,
          company: company.id,
          account: accountId,
          investment_id: createdInvestment.id,
          is_loan_transaction: false,
          transaction_status: "Completed",
          transaction_type: "Investment Deposit",
          notes: values.notes || `Investment deposit for product ${product.name}`,
          payment_method: values.payment_method,
          reference_number: values.reference_number || `INV-${Date.now()}`,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      await TransactionHooks.onInvestmentDeposit({
        investmentId: createdInvestment.id,
        amount: principal,
        date: new Date(investmentDate),
        company: company.id,
        token,
        accountId,
      });

      message.success("Investment created successfully");
      setShowInvestmentModal(false);
      investmentForm.resetFields();
      fetchCompanyInvestments();
      fetchInvestorProfiles();
      fetchInvestors();
    } catch (err) {
      message.error(
        err.response?.data?.errors?.[0]?.message ||
          "Failed to create investment"
      );
    } finally {
      setCreating(false);
    }
  };

  const investmentMap = companyInvestments.reduce((acc, inv) => {
    const userId = inv.investor_id;
    if (!userId) return acc;
    acc[userId] = acc[userId] || [];
    acc[userId].push(inv);
    return acc;
  }, {});

  const profileUsers = investorProfiles
    .map((profile) => ({
      ...profile.user,
      user_id: profile.user?.id,
      profile_id: profile.id,
    }))
    .filter((user) => user?.user_id);

  const mergedInvestorsMap = new Map();
  [...investors, ...profileUsers].forEach((inv) => {
    if (!inv?.user_id) return;
    if (!mergedInvestorsMap.has(inv.user_id)) {
      mergedInvestorsMap.set(inv.user_id, inv);
    }
  });

  const mergedInvestors = Array.from(mergedInvestorsMap.values()).map((inv) => {
    const invs = investmentMap[inv.user_id] || [];
    return {
      ...inv,
      investments: invs,
      total_invested: invs.reduce((sum, i) => sum + toNumber(i.principal_amount), 0),
    };
  });

  const filteredData = mergedInvestors.filter((investor) =>
    `${investor.first_name} ${investor.last_name} ${investor.company || ""} ${investor.email || ""} ${investor.phone || ""}`
      .toLowerCase()
      .includes(searchText.toLowerCase())
  );

  const investorOptions = mergedInvestors.map((inv) => {
    if (inv.profile_id) {
      return {
        value: `profile:${inv.profile_id}`,
        label: `${inv.first_name} ${inv.last_name} (${inv.phone || inv.email || "No contact"})`,
      };
    }
    return {
      value: `user:${inv.user_id}`,
      label: `${inv.first_name} ${inv.last_name} (${inv.phone || inv.email || "No contact"})`,
    };
  });

  return (
    <div style={{ maxWidth: 1400, margin: "0 auto" }}>
      {/* Summary Cards */}

      {getSummaryStats()}
      <Card
        bordered={false}
        style={{
          borderRadius: 12,
          boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
          padding: "24px 32px",
        }}
      >
        <Row
          justify="space-between"
          align="middle"
          style={{ marginBottom: 32 }}
        >
          <Col>
            <Space align="center" size={16}>
              <Title level={3} style={{ margin: 0 }}>
                Investor Portfolio
              </Title>
              <Statistic
                prefix={<LineChartOutlined />}
                value={mergedInvestors.length}
                suffix="investors"
                valueStyle={{ fontSize: 16, color: "#595959" }}
              />
            </Space>
          </Col>
          <Col>
            <Space size={16}>
              <Input
                placeholder="Search investors..."
                prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
                onChange={(e) => setSearchText(e.target.value)}
                style={{
                  width: 280,
                  borderRadius: 8,
                  border: "1px solid #f0f0f0",
                }}
              />
              <Button
                icon={<UserAddOutlined />}
                onClick={() => setShowInvestorModal(true)}
                style={{ borderRadius: 8, height: 40 }}
              >
                New Investor
              </Button>
              <Button
                type="primary"
                icon={<DollarOutlined />}
                onClick={() => setShowInvestmentModal(true)}
                style={{ borderRadius: 8, height: 40 }}
              >
                New Investment
              </Button>
            </Space>
          </Col>
        </Row>

        <Table
          columns={columns}
          dataSource={filteredData}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            pageSizeOptions: ["10", "25", "50"],
            showTotal: (total) => `Total ${total} investors`,
            style: { marginRight: 8 },
          }}
          scroll={{ x: true }}
          bordered={false}
          style={{
            border: "1px solid #f0f0f0",
            borderRadius: 8,
          }}
        />
      </Card>

      <Modal
        title="Add Investor"
        open={showInvestorModal}
        onCancel={() => setShowInvestorModal(false)}
        footer={null}
        width={720}
      >
        <Form form={investorForm} layout="vertical" onFinish={handleCreateInvestor}>
          <Form.Item label="Investor Source">
            <Radio.Group
              value={investorSource}
              onChange={(e) => setInvestorSource(e.target.value)}
            >
              <Radio value="new">New Investor</Radio>
              <Radio value="borrower">Existing Borrower</Radio>
            </Radio.Group>
          </Form.Item>

          {investorSource === "borrower" ? (
            <Form.Item
              label="Select Borrower"
              name="borrower_id"
              rules={[{ required: true, message: "Please select a borrower" }]}
            >
              <Select
                showSearch
                optionFilterProp="label"
                options={borrowers.map((borrower) => ({
                  value: borrower.id,
                  label: `${borrower.first_name} ${borrower.last_name} (${borrower.phone || borrower.email || "No contact"})`,
                }))}
              />
            </Form.Item>
          ) : (
            <>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    label="First Name"
                    name="first_name"
                    rules={[{ required: true, message: "First name required" }]}
                  >
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Last Name"
                    name="last_name"
                    rules={[{ required: true, message: "Last name required" }]}
                  >
                    <Input />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    label="Email"
                    name="email"
                    rules={[{ type: "email", required: true, message: "Valid email required" }]}
                  >
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Phone"
                    name="phone"
                    rules={[{ required: true, message: "Phone required" }]}
                  >
                    <Input />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item label="NRC/ID" name="nrc">
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="Temporary Password" name="password">
                    <Input.Password placeholder="Default: 123456" />
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item label="Address" name="address">
                <Input.TextArea rows={2} />
              </Form.Item>
            </>
          )}

          <Form.Item>
            <Button type="primary" htmlType="submit" loading={creating} block>
              Create Investor
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Add Investment"
        open={showInvestmentModal}
        onCancel={() => setShowInvestmentModal(false)}
        footer={null}
        width={900}
      >
        <Form form={investmentForm} layout="vertical" onFinish={handleCreateInvestment}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Investor"
                name="investor_id"
                rules={[{ required: true, message: "Select an investor" }]}
              >
                <Select
                  showSearch
                  optionFilterProp="label"
                  options={investorOptions}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Investment Product"
                name="investment_product"
                rules={[{ required: true, message: "Select a product" }]}
              >
                <Select
                  showSearch
                  optionFilterProp="label"
                  options={investmentProducts.map((product) => ({
                    value: product.id,
                    label: `${product.name} (${product.interest_rate}% ${product.interest_type})`,
                  }))}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                label="Principal Amount (ZMW)"
                name="principal_amount"
                rules={[{ required: true, message: "Enter amount" }]}
              >
                <InputNumber min={0} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Investment Date"
                name="investment_date"
                rules={[{ required: true, message: "Select date" }]}
              >
                <DatePicker style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Account to Credit"
                name="account_id"
                rules={[{ required: true, message: "Select account" }]}
              >
                <Select
                  options={(accounts || []).map((acc) => ({
                    value: acc.id,
                    label: `${acc.name || acc.account_name} (Balance: ZMW ${toNumber(acc.balance).toLocaleString()})`,
                  }))}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item label="Payment Method" name="payment_method">
                <Select
                  options={[
                    { label: "Bank Transfer", value: "bank_transfer" },
                    { label: "Mobile Money", value: "mobile_money" },
                    { label: "Cash", value: "cash" },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="Reference Number" name="reference_number">
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="Notes" name="notes">
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item shouldUpdate>
            {() => {
              const productId = investmentForm.getFieldValue("investment_product");
              const principal = investmentForm.getFieldValue("principal_amount");
              const investmentDate = investmentForm.getFieldValue("investment_date");
              const product = investmentProducts.find((p) => p.id === productId);
              if (!product || !principal || !investmentDate) return null;

              const termDays = convertTermToDays(product.term_days, product.term_unit);
              const maturityPreview = calculateMaturityAmounts({
                principal,
                annualRatePercent: product.interest_rate,
                days: termDays,
                interestType: product.interest_type,
                compoundFrequency: product.compound_frequency,
                withholdingTaxRate: product.withholding_tax_rate || 15,
              });
              const projectedValue = calculateAccruedValue({
                principal,
                annualRatePercent: product.interest_rate,
                startDate: investmentDate.toDate(),
                interestType: product.interest_type,
                compoundFrequency: product.compound_frequency,
              });

              return (
                <Card size="small" style={{ marginBottom: 16 }}>
                  <Row gutter={16}>
                    <Col span={8}>
                      <Text type="secondary">Projected Maturity (Gross)</Text>
                      <div>
                        <Text strong>
                          ZMW {maturityPreview.grossMaturity.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                        </Text>
                      </div>
                    </Col>
                    <Col span={8}>
                      <Text type="secondary">Withholding Tax</Text>
                      <div>
                        <Text strong>
                          ZMW {maturityPreview.withholdingTax.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                        </Text>
                      </div>
                    </Col>
                    <Col span={8}>
                      <Text type="secondary">Current Accrued Value</Text>
                      <div>
                        <Text strong>
                          ZMW {projectedValue.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                        </Text>
                      </div>
                    </Col>
                  </Row>
                </Card>
              );
            }}
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" loading={creating} block>
              Create Investment
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

// ─── Jutem Fund Investors View (Company 22) ────────────────────────────────

const zambianColors = {
  green: "#198754",
  orange: "#de7921",
  red: "#da291c",
  black: "#000000",
};

const JutemInvestorsView = () => {
  const { company } = useContext(AppContext);

  const [investors, setJutemInvestors] = useState([]);
  const [investments, setJutemInvestments] = useState([]);
  const [interestExpenses, setInterestExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [showAddInvestorModal, setShowAddInvestorModal] = useState(false);
  const [showAddInvestmentModal, setShowAddInvestmentModal] = useState(false);
  const [creatingInvestor, setCreatingInvestor] = useState(false);
  const [creatingInvestment, setCreatingInvestment] = useState(false);
  const [investorForm] = Form.useForm();
  const [investmentForm] = Form.useForm();

  const DIRECTUS_URL = "https://zedlenders.pickmesms.com";

  // Calculate interest expense for an investment
  const calculateInterestExpense = (investment) => {
    if (!investment.principal_amount || !investment.interest_rate) return 0;

    const principal = parseFloat(investment.principal_amount);
    const rate = parseFloat(investment.interest_rate) / 100;

    // Simple interest calculation
    const interest = principal * rate;
    return Math.round(interest * 100) / 100;
  };

  // Calculate accrued interest based on time elapsed
  const calculateAccruedInterest = (investment) => {
    if (!investment.principal_amount || !investment.interest_rate || !investment.investment_date) {
      return 0;
    }

    const principal = parseFloat(investment.principal_amount);
    const annualRate = parseFloat(investment.interest_rate) / 100;
    const startDate = moment(investment.investment_date);
    const maturityDate = investment.maturity_date ? moment(investment.maturity_date) : null;
    const today = moment();

    // Calculate based on payment frequency
    if (maturityDate && today.isAfter(maturityDate)) {
      // Investment has matured - return full interest
      return principal * annualRate;
    } else {
      // Calculate prorated interest for active investments
      const daysElapsed = today.diff(startDate, 'days');
      const daysInYear = 365;
      const accruedInterest = (principal * annualRate * daysElapsed) / daysInYear;
      return Math.round(accruedInterest * 100) / 100;
    }
  };

  // Check if investment is matured
  const isInvestmentMatured = (investment) => {
    if (!investment.maturity_date) return false;
    return moment().isAfter(moment(investment.maturity_date));
  };

  useEffect(() => {
    fetchData();
  }, [company]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };

      const [investorsRes, investmentsRes, expensesRes] = await Promise.all([
        axios.get(`${DIRECTUS_URL}/items/jutem_investors`, {
          headers,
          params: {
            filter: { company: { _eq: 22 } },
            fields: "id,name,status",
            limit: -1,
          },
        }),
        axios.get(`${DIRECTUS_URL}/items/jutem_investments`, {
          headers,
          params: {
            filter: { company: { _eq: 22 } },
            fields: "id,reference_number,investor.id,investor.name,principal_amount,investment_date,year,status",
            limit: -1,
          },
        }),
        axios.get(`${DIRECTUS_URL}/items/transactions`, {
          headers,
          params: {
            filter: {
              company: { _eq: 22 },
              transaction_type: { _eq: "interest_expense" },
            },
            fields: "id,reference_number,notes,amount,transaction_date,investment_reference",
            limit: -1,
          },
        }),
      ]);

      setJutemInvestors(investorsRes.data.data || []);
      setJutemInvestments(investmentsRes.data.data || []);
      setInterestExpenses(expensesRes.data.data || []);
    } catch (error) {
      console.error("Error fetching Jutem investor data:", error);
      message.error("Failed to load investors data");
    } finally {
      setLoading(false);
    }
  };

  // Build investor → investments mapping
  const investorData = useMemo(() => {
    const investorMap = {};
    investors.forEach((inv) => {
      investorMap[inv.id] = {
        ...inv,
        investments: [],
        totalInvested: 0,
        totalInterestExpenses: 0,
      };
    });

    investments.forEach((investment) => {
      const investorId = investment.investor?.id;
      if (investorId && investorMap[investorId]) {
        investorMap[investorId].investments.push(investment);
        investorMap[investorId].totalInvested += parseFloat(investment.principal_amount || 0);
      }
    });

    // Map interest expenses to investors via investment_reference (investment ID)
    const investmentIdToInvestor = {};
    investments.forEach((inv) => {
      if (inv.investor?.id && inv.id) {
        investmentIdToInvestor[String(inv.id)] = inv.investor.id;
      }
    });

    interestExpenses.forEach((exp) => {
      const investmentId = String(exp.investment_reference || "");
      const investorId = investmentIdToInvestor[investmentId];

      if (investorId && investorMap[investorId]) {
        investorMap[investorId].totalInterestExpenses += parseFloat(exp.amount || 0);
      }
    });

    return Object.values(investorMap);
  }, [investors, investments, interestExpenses]);

  const availableYears = useMemo(() => {
    const years = [...new Set(investments.map((inv) => inv.year).filter(Boolean))].sort().reverse();
    return years;
  }, [investments]);

  const filteredData = useMemo(() => {
    let filtered = [...investorData];

    if (searchText) {
      const search = searchText.toLowerCase();
      filtered = filtered.filter((inv) =>
        (inv.name || "").toLowerCase().includes(search)
      );
    }

    if (selectedYear) {
      filtered = filtered.filter((inv) =>
        inv.investments.some((investment) => investment.year === selectedYear)
      );
    }

    if (selectedStatus) {
      filtered = filtered.filter((inv) =>
        (inv.status || "").toLowerCase() === selectedStatus.toLowerCase()
      );
    }

    return filtered;
  }, [investorData, searchText, selectedYear, selectedStatus]);

  const stats = useMemo(() => {
    const totalInvested = filteredData.reduce((sum, inv) => sum + inv.totalInvested, 0);
    const totalInterestExpenses = filteredData.reduce((sum, inv) => sum + inv.totalInterestExpenses, 0);
    const totalInvestors = filteredData.length;
    const totalInvestmentCount = filteredData.reduce((sum, inv) => sum + inv.investments.length, 0);

    return { totalInvested, totalInterestExpenses, totalInvestors, totalInvestmentCount };
  }, [filteredData]);

  const fmtK = (val) => {
    const num = parseFloat(val || 0);
    return `K ${num.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const handleExport = () => {
    if (filteredData.length === 0) {
      message.warning("No data to export");
      return;
    }

    const exportData = filteredData.map((inv, i) => ({
      "#": i + 1,
      Investor: inv.name || "",
      Status: inv.status || "",
      "Total Investments": inv.investments.length,
      "Total Invested (ZMW)": inv.totalInvested.toFixed(2),
      "Total Interest Expenses (ZMW)": inv.totalInterestExpenses.toFixed(2),
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Jutem Investors");
    XLSX.writeFile(wb, `Jutem_Investors_${moment().format("YYYY-MM-DD")}.xlsx`);
    message.success("Exported successfully");
  };

  // Record interest expense for a matured investment
  const handleRecordInterestExpense = async (investment) => {
    try {
      const token = localStorage.getItem("token");
      const interestAmount = calculateInterestExpense(investment);
      const totalPayout = parseFloat(investment.principal_amount) + interestAmount;

      // Confirm with user
      const confirmed = window.confirm(
        `Record interest expense for ${investment.reference_number}?\n\n` +
        `Principal: ZMW ${parseFloat(investment.principal_amount).toLocaleString()}\n` +
        `Interest (${investment.interest_rate}%): ZMW ${interestAmount.toLocaleString()}\n` +
        `Total Payout: ZMW ${totalPayout.toLocaleString()}\n\n` +
        `This will create a transaction record and mark the investment as "Matured".`
      );

      if (!confirmed) return;

      // Create interest expense transaction
      await axios.post(
        `${DIRECTUS_URL}/items/transactions`,
        {
          transaction_type: "interest_expense",
          amount: interestAmount,
          transaction_date: moment().format("YYYY-MM-DD"),
          transaction_status: "Pending",
          reference_number: `INT-${investment.reference_number}`,
          notes: `Interest expense for investment ${investment.reference_number} - Principal: ZMW ${investment.principal_amount}, Rate: ${investment.interest_rate}%`,
          investment_reference: investment.reference_number,
          company: 22,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Update investment status to "Matured"
      await axios.patch(
        `${DIRECTUS_URL}/items/jutem_investments/${investment.id}`,
        {
          status: "Matured",
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      message.success(`Interest expense recorded: ZMW ${interestAmount.toLocaleString()}`);
      fetchData(); // Refresh data
    } catch (err) {
      console.error("Error recording interest expense:", err);
      message.error(
        err.response?.data?.errors?.[0]?.message || "Failed to record interest expense"
      );
    }
  };

  const handleAddInvestor = async (values) => {
    try {
      setCreatingInvestor(true);
      const token = localStorage.getItem("token");

      await axios.post(
        `${DIRECTUS_URL}/items/jutem_investors`,
        {
          name: values.name.trim(),
          status: "Active",
          company: 22,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      message.success("Investor added successfully");
      setShowAddInvestorModal(false);
      investorForm.resetFields();
      fetchData();
    } catch (err) {
      message.error(
        err.response?.data?.errors?.[0]?.message || "Failed to add investor"
      );
    } finally {
      setCreatingInvestor(false);
    }
  };

  const handleAddInvestment = async (values) => {
    try {
      setCreatingInvestment(true);
      const token = localStorage.getItem("token");

      const investorRecord = investors.find((inv) => inv.id === values.investor);
      const investorName = (investorRecord?.name || "UNKNOWN").toUpperCase().replace(/\s+/g, "");
      const year = values.investment_date.year();
      const existingCount = investments.filter(
        (inv) => inv.investor?.id === values.investor && inv.year === year
      ).length;
      const refNumber = `JINV-${investorName}-${year}-${String(existingCount + 1).padStart(3, "0")}`;

      await axios.post(
        `${DIRECTUS_URL}/items/jutem_investments`,
        {
          reference_number: refNumber,
          investor: values.investor,
          principal_amount: values.principal_amount,
          investment_date: values.investment_date.format("YYYY-MM-DD"),
          year: year,
          status: values.status || "Active",
          company: 22,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      message.success("Investment added successfully");
      setShowAddInvestmentModal(false);
      investmentForm.resetFields();
      fetchData();
    } catch (err) {
      message.error(
        err.response?.data?.errors?.[0]?.message || "Failed to add investment"
      );
    } finally {
      setCreatingInvestment(false);
    }
  };

  const expandedRowRender = (record) => {
    if (record.investments.length === 0) {
      return (
        <div style={{ padding: 16, textAlign: "center" }}>
          <Text type="secondary">No investments found for this investor</Text>
        </div>
      );
    }

    const cols = [
      {
        title: "Reference",
        dataIndex: "reference_number",
        key: "reference_number",
        render: (v) => <Text code>{v}</Text>,
      },
      {
        title: "Principal Amount",
        dataIndex: "principal_amount",
        key: "principal_amount",
        render: (v) => fmtK(v),
        align: "right",
      },
      {
        title: "Investment Date",
        dataIndex: "investment_date",
        key: "investment_date",
        render: (v) => (v ? moment(v).format("DD MMM YYYY") : "-"),
      },
      {
        title: "Year",
        dataIndex: "year",
        key: "year",
        align: "center",
      },
      {
        title: "Status",
        dataIndex: "status",
        key: "status",
        render: (s) => {
          const statusLower = (s || "").toLowerCase();
          let color = "default";
          if (statusLower === "active" || statusLower === "ongoing") color = "blue";
          if (statusLower === "matured") color = "green";
          if (statusLower === "returned") color = "cyan";
          if (statusLower === "defaulted") color = "red";
          return <Tag color={color}>{s || "N/A"}</Tag>;
        },
      },
    ];

    return (
      <Table
        columns={cols}
        dataSource={record.investments}
        rowKey="id"
        pagination={false}
        size="small"
        style={{ margin: "0 16px 16px 16px" }}
      />
    );
  };

  const columns = [
    {
      title: "Investor",
      key: "name",
      render: (_, record) => (
        <Space>
          <Text strong>{record.name || "N/A"}</Text>
          {record.status?.toLowerCase() === "active" && (
            <Tag color="processing">ACTIVE</Tag>
          )}
        </Space>
      ),
      sorter: (a, b) => (a.name || "").localeCompare(b.name || ""),
    },
    {
      title: "Investments",
      key: "investment_count",
      render: (_, record) => record.investments.length,
      align: "center",
    },
    {
      title: "Total Invested",
      key: "total_invested",
      render: (_, record) => (
        <Text strong style={{ color: "#1890ff" }}>
          {fmtK(record.totalInvested)}
        </Text>
      ),
      sorter: (a, b) => a.totalInvested - b.totalInvested,
      align: "right",
    },
    {
      title: "Interest Expenses",
      key: "interest_expenses",
      render: (_, record) => (
        <Text style={{ color: zambianColors.red }}>
          {fmtK(record.totalInterestExpenses)}
        </Text>
      ),
      sorter: (a, b) => a.totalInterestExpenses - b.totalInterestExpenses,
      align: "right",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (s) => {
        const statusLower = (s || "").toLowerCase();
        let color = "default";
        if (statusLower === "active") color = "green";
        if (statusLower === "inactive") color = "orange";
        return <Tag color={color}>{s || "N/A"}</Tag>;
      },
    },
  ];

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: 100 }}>
        <Spin size="large" />
        <div style={{ marginTop: 16 }}>
          <Text type="secondary">Loading Jutem Investors...</Text>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={2} style={{ margin: 0 }}>
            <FundOutlined style={{ marginRight: 12, color: zambianColors.green }} />
            Jutem Fund Investors
          </Title>
        </Col>
        <Col>
          <Space>
            <Button
              icon={<UserAddOutlined />}
              onClick={() => setShowAddInvestorModal(true)}
              style={{ borderRadius: 8, height: 40 }}
            >
              New Investor
            </Button>
            <Button
              type="primary"
              icon={<DollarOutlined />}
              onClick={() => setShowAddInvestmentModal(true)}
              style={{ borderRadius: 8, height: 40 }}
            >
              New Investment
            </Button>
          </Space>
        </Col>
      </Row>

      {/* Statistics */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={6}>
          <Card
            bordered={false}
            style={{
              borderRadius: 8,
              background: zambianColors.green,
              boxShadow: "0 2px 8px rgba(25, 135, 84, 0.2)",
              minHeight: 130,
            }}
          >
            <Statistic
              title={<span style={{ color: "#fff", fontSize: 14, fontWeight: 500 }}>Total Investors</span>}
              value={stats.totalInvestors}
              prefix={<UserOutlined style={{ color: "#fff", fontSize: 24 }} />}
              valueStyle={{ color: "#fff", fontSize: 22, fontWeight: "bold" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card
            bordered={false}
            style={{
              borderRadius: 8,
              background: "#1890ff",
              boxShadow: "0 2px 8px rgba(24, 144, 255, 0.2)",
              minHeight: 130,
            }}
          >
            <Statistic
              title={<span style={{ color: "#fff", fontSize: 14, fontWeight: 500 }}>Total Invested</span>}
              value={stats.totalInvested}
              precision={2}
              prefix={<DollarOutlined style={{ color: "#fff", fontSize: 24 }} />}
              valueStyle={{ color: "#fff", fontSize: 22, fontWeight: "bold" }}
              suffix={<span style={{ color: "#fff", fontSize: 14 }}>ZMW</span>}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card
            bordered={false}
            style={{
              borderRadius: 8,
              background: "#52c41a",
              boxShadow: "0 2px 8px rgba(82, 196, 26, 0.2)",
              minHeight: 130,
            }}
          >
            <Statistic
              title={<span style={{ color: "#fff", fontSize: 14, fontWeight: 500 }}>Total Investments</span>}
              value={stats.totalInvestmentCount}
              valueStyle={{ color: "#fff", fontSize: 22, fontWeight: "bold" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card
            bordered={false}
            style={{
              borderRadius: 8,
              background: zambianColors.red,
              boxShadow: "0 2px 8px rgba(218, 41, 28, 0.2)",
              minHeight: 130,
            }}
          >
            <Statistic
              title={<span style={{ color: "#fff", fontSize: 14, fontWeight: 500 }}>Total Interest Expenses</span>}
              value={stats.totalInterestExpenses}
              precision={2}
              prefix={<RiseOutlined style={{ color: "#fff", fontSize: 24 }} />}
              valueStyle={{ color: "#fff", fontSize: 22, fontWeight: "bold" }}
              suffix={<span style={{ color: "#fff", fontSize: 14 }}>ZMW</span>}
            />
          </Card>
        </Col>
      </Row>

      {/* Filters */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Row gutter={[12, 12]} align="middle">
          <Col xs={24} sm={8} md={6}>
            <Input
              placeholder="Search by investor name..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={12} sm={6} md={4}>
            <Select
              placeholder="Year"
              value={selectedYear}
              onChange={setSelectedYear}
              allowClear
              style={{ width: "100%" }}
            >
              {availableYears.map((y) => (
                <Select.Option key={y} value={y}>
                  {y}
                </Select.Option>
              ))}
            </Select>
          </Col>
          <Col xs={12} sm={6} md={4}>
            <Select
              placeholder="Status"
              value={selectedStatus}
              onChange={setSelectedStatus}
              allowClear
              style={{ width: "100%" }}
            >
              <Select.Option value="active">Active</Select.Option>
              <Select.Option value="inactive">Inactive</Select.Option>
            </Select>
          </Col>
          <Col xs={24} sm={4} md={4}>
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              onClick={handleExport}
              block
              style={{ background: zambianColors.green, borderColor: zambianColors.green }}
            >
              Export
            </Button>
          </Col>
        </Row>
      </Card>

      {/* Table */}
      <Card>
        <Table
          columns={columns}
          dataSource={filteredData}
          rowKey="id"
          expandable={{
            expandedRowRender,
            rowExpandable: () => true,
          }}
          pagination={{
            pageSize: 20,
            showSizeChanger: true,
            showTotal: (total) => `${total} investors`,
          }}
          scroll={{ x: true }}
          summary={() => {
            const totalPrincipal = filteredData.reduce((s, inv) => s + inv.totalInvested, 0);
            const totalExpenses = filteredData.reduce((s, inv) => s + inv.totalInterestExpenses, 0);

            return (
              <Table.Summary fixed>
                <Table.Summary.Row style={{ fontWeight: "bold" }}>
                  <Table.Summary.Cell index={0}>TOTAL</Table.Summary.Cell>
                  <Table.Summary.Cell index={1} align="center">
                    {filteredData.reduce((s, inv) => s + inv.investments.length, 0)}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={2} align="right">
                    {fmtK(totalPrincipal)}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={3} align="right">
                    {fmtK(totalExpenses)}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={4} />
                </Table.Summary.Row>
              </Table.Summary>
            );
          }}
        />
      </Card>

      {/* Add Investor Modal */}
      <Modal
        title="Add Investor"
        open={showAddInvestorModal}
        onCancel={() => {
          setShowAddInvestorModal(false);
          investorForm.resetFields();
        }}
        footer={null}
        width={480}
      >
        <Form form={investorForm} layout="vertical" onFinish={handleAddInvestor}>
          <Form.Item
            label="Investor Name"
            name="name"
            rules={[{ required: true, message: "Please enter investor name" }]}
          >
            <Input placeholder="e.g., Kaya" />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={creatingInvestor} block>
              Add Investor
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* Add Investment Modal */}
      <Modal
        title="Add Investment"
        open={showAddInvestmentModal}
        onCancel={() => {
          setShowAddInvestmentModal(false);
          investmentForm.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form form={investmentForm} layout="vertical" onFinish={handleAddInvestment}>
          <Form.Item
            label="Investor"
            name="investor"
            rules={[{ required: true, message: "Please select an investor" }]}
          >
            <Select
              showSearch
              optionFilterProp="label"
              placeholder="Select investor"
              options={investors.map((inv) => ({
                value: inv.id,
                label: inv.name,
              }))}
            />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Principal Amount (ZMW)"
                name="principal_amount"
                rules={[{ required: true, message: "Please enter amount" }]}
              >
                <InputNumber
                  min={0}
                  style={{ width: "100%" }}
                  formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                  parser={(value) => value.replace(/,/g, "")}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Investment Date"
                name="investment_date"
                rules={[{ required: true, message: "Please select date" }]}
              >
                <DatePicker style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label="Status"
            name="status"
            initialValue="Active"
          >
            <Select>
              <Select.Option value="Active">Active</Select.Option>
              <Select.Option value="Matured">Matured</Select.Option>
              <Select.Option value="Returned">Returned</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" loading={creatingInvestment} block>
              Add Investment
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default InvestorsRouter;
